
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../../../components/contextual-app-bar/ViewOptionStateEnum";
import { CustomDurationDialogValues } from "../../../../components/custom-duration-dialog/CustomDurationDialog";
import CustomDurationUtils, { DurationOptionEnum } from "../../../../components/custom-duration-dialog/CustomDurationUtils";
import { addHours } from "date-fns";
import _ from "lodash";
import { IeLynxObject } from "../../../../common/models/eLynxObject";

export type AlarmDashboardDetailStateType = {
    viewOptionState: ViewOptionStateEnum,
    durationLabel: string,
    durationOptions: CustomDurationDialogValues;
    showVoice: boolean;
    showText: boolean;
    showEmail: boolean;
    selectedTarget: IeLynxObject | undefined;
};

export let defaultAlarmDashboardDetailState: AlarmDashboardDetailStateType = {
    viewOptionState: ViewOptionStateEnum.Default,
    durationLabel: CustomDurationUtils.getCustomDurationLabel(DurationOptionEnum.OneDay, true),
    durationOptions: {
        option: DurationOptionEnum.OneDay,
        duration: {
            startDate: addHours(new Date(), -24),
            endDate: new Date()
        }
    },
    showEmail: true,
    showText: true,
    showVoice: true,
    selectedTarget: undefined

};

enum AlarmDashboardDetailStateActionType {

    DurationChanged = 'ALARM_DASHBOARD_DURATION_CHANGED',
    ShowEmailChanged = 'ALARM_DASHBOARD_EMAIL_CHANGED',
    ShowTextChanged = 'ALARM_DASHBOARD_TEXT_CHANGED',
    ShowVoiceChanged = 'ALARM_DASHBOARD_VOICE_CHANGED',
    SelectedTargetChanged = 'ALARM_DASHBOARD_SELECTED_TARGET_CHANGED',
    ResetChanged = 'ALARM_DASHBOARD_RESET_CHANGED'
}

type AlarmDashboardDetailStateActions = {
    type: AlarmDashboardDetailStateActionType.DurationChanged;
    payload: CustomDurationDialogValues;
} | {
    type: AlarmDashboardDetailStateActionType.ShowEmailChanged;
    payload: boolean;
} | {
    type: AlarmDashboardDetailStateActionType.ShowTextChanged;
    payload: boolean;
} | {
    type: AlarmDashboardDetailStateActionType.ShowVoiceChanged;
    payload: boolean;
} | {
    type: AlarmDashboardDetailStateActionType.SelectedTargetChanged;
    payload: IeLynxObject | undefined;
} | {
    type: AlarmDashboardDetailStateActionType.ResetChanged;
    payload: void;
};


const optionsChanged = (newState: AlarmDashboardDetailStateType, 
    defaultState: AlarmDashboardDetailStateType) => 
    newState.durationOptions.option !== defaultState.durationOptions.option ||
    newState.showText !== defaultState.showText || 
    newState.showEmail !== defaultState.showEmail || 
    newState.showVoice !== defaultState.showVoice;

function AlarmDashboardDetailStateReducer(state: AlarmDashboardDetailStateType, action: AlarmDashboardDetailStateActions): AlarmDashboardDetailStateType {

    const copy = _.cloneDeep(state);

    switch (action.type) {
        
        case AlarmDashboardDetailStateActionType.DurationChanged:

            copy.durationLabel = CustomDurationUtils.getCustomDurationLabel(action.payload.option, true);
            copy.durationOptions =  {
                option: action.payload.option,
                duration: {
                    startDate: new Date(action.payload.duration.startDate as Date),
                    endDate: new Date(action.payload.duration.endDate as Date)
                }
            };

            copy.viewOptionState = optionsChanged(copy, defaultAlarmDashboardDetailState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;

            return copy;

        case AlarmDashboardDetailStateActionType.SelectedTargetChanged:
            copy.selectedTarget = action.payload;
            return copy;
           
        case AlarmDashboardDetailStateActionType.ShowEmailChanged:
            copy.showEmail = action.payload;
            copy.viewOptionState = optionsChanged(copy, defaultAlarmDashboardDetailState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;            

        case AlarmDashboardDetailStateActionType.ShowTextChanged:
            copy.showText = action.payload;
            copy.viewOptionState = optionsChanged(copy, defaultAlarmDashboardDetailState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;

        case AlarmDashboardDetailStateActionType.ShowVoiceChanged:
            copy.showVoice = action.payload;
            copy.viewOptionState = optionsChanged(copy, defaultAlarmDashboardDetailState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;            
    
                
        case AlarmDashboardDetailStateActionType.ResetChanged:
            return {...defaultAlarmDashboardDetailState};

        default:
            return state;
    }
}

export default function useAlarmDashboardDetailState() {

    const [state, dispatch] = useReducer(AlarmDashboardDetailStateReducer, {...defaultAlarmDashboardDetailState});

    const setDuration = useCallback((value: CustomDurationDialogValues) => dispatch({
        type: AlarmDashboardDetailStateActionType.DurationChanged,
        payload: value
    }), []);


    const setShowEmail = useCallback((value: boolean) => dispatch({
        type: AlarmDashboardDetailStateActionType.ShowEmailChanged,
        payload: value
    }), []);

    const setShowText = useCallback((value: boolean) => dispatch({
        type: AlarmDashboardDetailStateActionType.ShowTextChanged,
        payload: value
    }), []);

    const setShowVoice = useCallback((value: boolean) => dispatch({
        type: AlarmDashboardDetailStateActionType.ShowVoiceChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: AlarmDashboardDetailStateActionType.ResetChanged,
        payload: undefined
    }), []);

    const setSelectedTarget = useCallback((item: IeLynxObject | undefined) => dispatch({
        type: AlarmDashboardDetailStateActionType.SelectedTargetChanged,
        payload: item
    }), []);


    return {
        state,
        setDuration,
        setShowEmail,
        setShowText,
        setShowVoice,
        setSelectedTarget,
        resetState
    };
}
